import { Component, OnInit, AfterViewInit, OnDestroy, ViewChild, TemplateRef } from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatDialog } from '@angular/material/dialog';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { MaterialModule } from '../material.module';
import { DataGridComponent, GridColumn, GridConfig } from '../shared/data-grid/data-grid.component';
import { SpinnerComponent } from '../shared/spinner/spinner.component';
import { ProjectsDashboardService, DashboardProject, ProjectSummary, DashboardTest } from '../services/projects-dashboard.service';
import { ProjectDetailsComponent } from '../project-details/project-details.component';
import { SlurryDetailsComponent } from '../project-details/slurry-details/slurry-details.component';
import { CreateNewPartDialogComponent } from './create-new-part-dialog/create-new-part-dialog.component';
import { CreateNewProjectDialogComponent } from './create-new-project-dialog/create-new-project-dialog.component';
import { NotesDialogComponent } from './notes-dialog/notes-dialog.component';
import { DashboardTreeService, TrashProjectRequest } from '../services/dashboard-tree.service';
import { WellAttachmentsDialogComponent } from './well-attachments-dialog/well-attachments-dialog.component';
import { AddRigComponent } from '../tools/add-rig/add-rig.component';
import { ChangeScalePortComponent } from '../tools/change-scale-port/change-scale-port.component';
import { NotesNotificationComponent } from '../tools/notes-notification/notes-notification.component';
import { WellUpdateComponent } from '../tools/well-update/well-update.component';
import { MetricsComponent } from '../tools/metrics/metrics.component';
import { BuRedesignMetricsComponent } from '../tools/bu-redesign-metrics/bu-redesign-metrics.component';
import { SearchComponent } from '../tools/search/search.component';
import { EmailSubscriptionsComponent } from '../tools/email-subscriptions/email-subscriptions.component';
import { WellSearchDialogComponent, WellSearchDialogResult } from './well-search-dialog/well-search-dialog.component';
import { ConfirmDialogComponent } from '../shared/confirm-dialog/confirm-dialog.component';
import { environment } from '../../environments/environment';
import { ManageMaterialsComponent } from '../tools/manage-materials/manage-materials.component';
import { ManageServiceCompaniesComponent } from '../tools/manage-service-companies/manage-service-companies.component';
import { SummaryReportByDateComponent } from '../tools/summary-report-by-date/summary-report-by-date.component';
import { MaterialsQAQC } from '../tools/materials-qaqc/materials-qaqc.component';
import { CisProSearchComponent } from '../project-details/cispro-search/cispro-search.component';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

type DashboardView = 'projects' | 'projectSummary' | 'tests';

export interface TreeProject {
  label: string;
  id: number;
  businessUnitCode: string;
  wellId: number;
  wellName: string;
  businessUnitName: string;
}

export interface TreeWell {
  label: string;
  wellId: number;
  projects: TreeProject[];
  expanded: boolean;
}

export interface TreeSbu {
  label: string;
  code: string;
  wells: TreeWell[];
  expanded: boolean;
}

export interface TreeSection {
  label: string;
  code: string;
  sbus: TreeSbu[];
  projects: TreeProject[];
  expanded: boolean;
}

type SelectionState =
  | { type: 'all' }
  | { type: 'section'; section: TreeSection }
  | { type: 'sbu'; sbu: TreeSbu }
  | { type: 'well'; well: TreeWell };

type DashboardRouteState = {
  openProjectId?: number;
  projectInitialTab?: 'project' | 'attachments';
};

@Component({
  selector: 'app-projects-dashboard',
  imports: [MaterialModule, DataGridComponent, SpinnerComponent, FormsModule, ProjectDetailsComponent, CreateNewProjectDialogComponent, RouterOutlet, MaterialsQAQC,
    AddRigComponent, ChangeScalePortComponent, NotesNotificationComponent, WellUpdateComponent, MetricsComponent, BuRedesignMetricsComponent, SlurryDetailsComponent],
  templateUrl: './projects-dashboard.component.html',
  styleUrls: ['./projects-dashboard.component.css'],
})
export class ProjectsDashboardComponent implements OnInit, AfterViewInit, OnDestroy {

  currentView: DashboardView = 'projects';
  isDetailsView = false;
  isCreateProjectView = false;
  selectedProjectId: number | null = null;
  projectDetailsInitialTab: 'project' | 'serviceCompany' | 'materialsQaqc' | 'attachments' = 'project';
  selectedCreateProjectId: number | null = null;

  // Embedded Slurry Details (shown in the content area instead of Project Details)
  isSlurryDetailsView = false;
  selectedSlurryId: number | null = null;
  selectedSlurryUfid = '';
  slurryProjectId: number | null = null;
  slurryProjectName = '';
  slurryProjectType: number | null = null;
  slurryRedesignComments = '';
  createProjectWellId: number = 0;
  createProjectWellName: string = '';
  createProjectBusinessUnitCode: string = '';
  createProjectBusinessUnitName: string = '';
  private currentSelection: SelectionState = { type: 'all' };
  private navigationSyncSubscription?: Subscription;

  @ViewChild('statusCellTemplate') statusCellTemplate!: TemplateRef<any>;
  @ViewChild('testStatusCellTemplate') testStatusCellTemplate!: TemplateRef<any>;
  @ViewChild('trashActionTemplate') trashActionTemplate!: TemplateRef<any>;
  @ViewChild('projectMenuTrigger') projectMenuTrigger!: MatMenuTrigger;
  @ViewChild('wellMenuTrigger') wellMenuTrigger!: MatMenuTrigger;
  @ViewChild('sectionMenuTrigger') sectionMenuTrigger!: MatMenuTrigger;
  @ViewChild('gridProjectMenuTrigger') gridProjectMenuTrigger!: MatMenuTrigger;

  contextMenuPosition = { x: '0px', y: '0px' };
  selectedContextProject: TreeProject | null = null;
  selectedContextWell: TreeWell | null = null;
  selectedContextSection: TreeSection | null = null;

  private readonly currentYear = new Date().getFullYear();
  private readonly startYear = environment.startYear;
  selectedYear: number = this.currentYear;
  years: number[] = Array.from({ length: this.currentYear - this.startYear + 1 }, (_, i) => this.startYear + i).reverse();
  selectedNode = 'All';
  selectedTool: string | null = null;
  allExpanded = false;
  sidebarCollapsed = false;

  treeSections: TreeSection[] = [];
  isLoading = false;

  data: DashboardProject[] = [];
  summaryData: ProjectSummary[] = [];
  testsData: DashboardTest[] = [];

  notesCount = 0;
  notesAddedToday = 0;
  notesList: any[] = [];

  gridConfig: GridConfig = {
    pageSize: 10,
    pageSizeOptions: [10, 20, 50],
    showFirstLastButtons: true,
    showColumnFilters: true,
    enableSorting: true,
    stickyHeader: true,
  };

  operationsColumns: GridColumn[] = [
    { key: 'statusColor', label: '', filterable: false, sortable: false, width: '35px', cellAlign: 'center' },
    { key: 'status', label: 'Status', filterable: true, sortable: true, width: '130px' },
    { key: 'name', label: 'UFID', filterable: true, sortable: true, width: '240px' },
    { key: 'businessUnitCode', label: 'SBU Code', filterable: true, sortable: true, width: '90px' },
    { key: 'wellName', label: 'Well Name', filterable: true, sortable: true, width: '180px' },
    { key: 'measuredDepth', label: 'MD', filterable: true, sortable: true, type: 'number', width: '70px' },
    { key: 'totalVerticalDepth', label: 'TVD', filterable: true, sortable: true, type: 'number', width: '70px' },
    { key: 'jobType', label: 'Job Type', filterable: true, sortable: true, width: '90px' },
    { key: 'jobSubType', label: 'Job Subtype', filterable: true, sortable: true, width: '120px' },
    { key: 'mudType', label: 'Mud Type', filterable: true, sortable: true, width: '110px' },
    { key: 'receivedBy', label: 'Received By', filterable: true, sortable: true, width: '110px' },
    { key: 'receiveDate', label: 'Receive Date', filterable: true, sortable: true, type: 'date', width: '120px' },
    { key: 'sentToLabDate', label: 'Sent To Lab Date', filterable: true, sortable: true, type: 'date', width: '145px', accessor: (item) => item.sentToLabDate ? item.sentToLabDate.substring(0, 10) : '' },
    { key: 'trashAction', label: 'Trash', filterable: false, sortable: false, width: '50px', cellAlign: 'center' }
  ];

  projectSummaryColumns: GridColumn[] = [
    { key: 'statusColor', label: '', filterable: false, sortable: false, width: '35px', cellAlign: 'center' },
    { key: 'status', label: 'Status', filterable: true, sortable: true, width: '130px' },
    { key: 'name', label: 'Project', filterable: true, sortable: true, width: '240px' },
    { key: 'sapCode', label: 'SAP Code', filterable: true, sortable: true, width: '200px' },
    { key: 'receiveDate', label: 'Receive Date', filterable: true, sortable: true, type: 'date', width: '120px' },
    { key: 'sbuName', label: 'OPCO=Profit Center', filterable: true, sortable: true, width: '280px' },
    { key: 'rig', label: 'Rig', filterable: true, sortable: true, width: '180px' },
    { key: 'wellName', label: 'Well Name', filterable: true, sortable: true, width: '180px' },
    { key: 'jobType', label: 'Job Description', filterable: true, sortable: true, width: '200px' }
  ];

  testsColumns: GridColumn[] = [
    { key: 'testStatusBadge', label: '', filterable: false, sortable: false, width: '35px', cellAlign: 'center' },
    { key: 'projectName', label: 'Project UFID', filterable: true, sortable: true, width: '240px' },
    { key: 'slurryName', label: 'Slurry Name', filterable: true, sortable: true, width: '120px' },
    { key: 'testTypeName', label: 'Test Type', filterable: true, sortable: true, width: '200px' },
    { key: 'rigName', label: 'Rig Name', filterable: true, sortable: true, width: '200px' },
    { key: 'wellName', label: 'Well Name', filterable: true, sortable: true, width: '180px' },
    { key: 'sentToLabDate', label: 'Sent To Lab Date', filterable: true, sortable: true, type: 'date', width: '145px', accessor: (item: DashboardTest) => item.sentToLabDate ? item.sentToLabDate.substring(0, 10) : '' },
    { key: 'lastUpdateDate', label: 'Last Update Date', filterable: true, sortable: true, type: 'date', width: '145px', accessor: (item: DashboardTest) => item.lastUpdateDate ? item.lastUpdateDate.substring(0, 10) : '' },
  ];


  constructor(private dashboardService: ProjectsDashboardService, private dashboardTreeService: DashboardTreeService, private dialog: MatDialog, private router: Router) { }

  ngOnInit(): void {
    this.loadHomeData(this.selectedYear);
    this.loadNotes();
    this.applyDashboardRouteState(history.state as DashboardRouteState);
    this.syncEmbeddedViewsWithRoute(this.router.url);
    this.navigationSyncSubscription = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(event => {
        this.syncEmbeddedViewsWithRoute(event.urlAfterRedirects);
        if (this.isDashboardBaseRoute(event.urlAfterRedirects)) {
          this.applyDashboardRouteState(history.state as DashboardRouteState);
        }
      });
  }

  ngOnDestroy(): void {
    this.navigationSyncSubscription?.unsubscribe();
  }

  private loadNotes(): void {
    this.dashboardService.getNotes().subscribe({
      next: (response) => {
        this.notesList = response.notes;
        this.notesCount = response.notes.length;
        this.notesAddedToday = response.notesAddedToday;
      },
      error: (err) => {
        console.error('Failed to load notes', err);
      }
    });
  }

  onYearChange(): void {
    this.ensureDashboardView();
    this.summaryData = [];
    this.testsData = [];
    this.currentSelection = { type: 'all' };
    this.selectedNode = 'All';
    if (this.currentView === 'projectSummary') {
      this.loadSummaryData(this.selectedYear);
    } else if (this.currentView === 'tests') {
      this.loadTestsData(this.selectedYear);
    } else {
      this.loadData(this.selectedYear);
    }
  }

  private loadHomeData(year: number, onLoaded?: () => void): void {
    this.isLoading = true;
    this.dashboardService.getHomeDashboard(year).subscribe({
      next: (response) => {
        const projects = response.projects;
        this.data = projects;
        this.treeSections = this.buildTreeFromProjects(projects);
        this.isLoading = false;
        onLoaded?.();
      },
      error: (err) => {
        console.error('Failed to load home dashboard data', err);
        this.isLoading = false;
      }
    });
  }

  private loadData(year: number, onLoaded?: () => void): void {
    this.isLoading = true;
    this.dashboardService.getProjectsByYear(year).subscribe({
      next: (projects) => {
        this.data = projects;
        this.treeSections = this.buildTreeFromProjects(projects);
        this.isLoading = false;
        onLoaded?.();
      },
      error: (err) => {
        console.error('Failed to load dashboard data', err);
        this.isLoading = false;
      }
    });
  }

  private buildTreeFromProjects(projects: DashboardProject[]): TreeSection[] {
    const sections: TreeSection[] = [];

    const allSbuProjects = projects.filter(p => !p.rAndDProject && p.businessUnitCode !== 'TR');
    if (allSbuProjects.length > 0) {
      const sbuMap = new Map<string, { name: string; wells: Map<string, DashboardProject[]> }>();
      for (const p of allSbuProjects) {
        if (!sbuMap.has(p.businessUnitCode)) {
          sbuMap.set(p.businessUnitCode, { name: p.businessUnitName, wells: new Map() });
        }
        const sbu = sbuMap.get(p.businessUnitCode)!;
        if (!sbu.wells.has(p.wellName)) {
          sbu.wells.set(p.wellName, []);
        }
        sbu.wells.get(p.wellName)!.push(p);
      }

      const sbus: TreeSbu[] = Array.from(sbuMap.entries()).map(([code, sbu]) => ({
        label: sbu.name,
        code,
        expanded: false,
        wells: Array.from(sbu.wells.entries()).map(([wellName, wellProjects]) => ({
          label: wellName,
          wellId: wellProjects[0].wellId,
          expanded: false,
          projects: wellProjects.map(p => ({ label: p.name, id: p.id, businessUnitCode: p.businessUnitCode, wellId: p.wellId, wellName: p.wellName, businessUnitName: p.businessUnitName }))
        }))
      }));

      sections.push({ label: 'All SBUs', code: '', sbus, projects: [], expanded: false });
    }

    // R&D: rAndDProject flag, excluding trashed projects
    const rdProjects = projects
      .filter(p => p.rAndDProject && p.businessUnitCode !== 'TR')
      .map(p => ({ label: p.name, id: p.id, businessUnitCode: p.businessUnitCode, wellId: p.wellId, wellName: p.wellName, businessUnitName: p.businessUnitName }));
    if (rdProjects.length > 0) {
      sections.push({ label: 'R&D', code: 'RD', sbus: [], projects: rdProjects, expanded: false });
    }

    // Trash: businessUnitCode === 'TR'
    const trashProjects = projects
      .filter(p => p.businessUnitCode === 'TR')
      .map(p => ({ label: p.name, id: p.id, businessUnitCode: p.businessUnitCode, wellId: p.wellId, wellName: p.wellName, businessUnitName: p.businessUnitName }));
    if (trashProjects.length > 0) {
      sections.push({ label: 'Trash', code: 'TR', sbus: [], projects: trashProjects, expanded: false });
    }

    return sections;
  }

  toggleAll(): void {
    this.allExpanded = !this.allExpanded;
  }

  selectAll(): void {
    this.ensureDashboardView();
    this.selectedNode = 'All';
    this.currentSelection = { type: 'all' };
    if (this.currentView === 'projectSummary') {
      this.loadSummaryData(this.selectedYear);
    } else if (this.currentView === 'tests') {
      this.loadTestsData(this.selectedYear);
    } else {
      this.loadHomeData(this.selectedYear);
    }
  }

  selectNode(label: string): void {
    this.selectedNode = label;
  }

  onProjectContextMenu(event: MouseEvent, project: TreeProject): void {
    event.preventDefault();
    event.stopPropagation();
    this.contextMenuPosition.x = event.clientX + 'px';
    this.contextMenuPosition.y = event.clientY + 'px';
    this.selectedContextProject = project;
    this.projectMenuTrigger.openMenu();
  }

  onGridProjectContextMenu({ row, event }: { row: DashboardProject; event: MouseEvent }): void {
    if (this.currentView !== 'projects')
      return;
    this.contextMenuPosition.x = event.clientX + 'px';
    this.contextMenuPosition.y = event.clientY + 'px';
    this.selectedContextProject = {
      id: row.id,
      label: row.name,
      businessUnitCode: row.businessUnitCode,
      wellId: row.wellId,
      wellName: row.wellName,
      businessUnitName: row.businessUnitName,
    };
    this.gridProjectMenuTrigger.openMenu();
  }

  onSectionContextMenu(event: MouseEvent, section: TreeSection): void {
    event.preventDefault();
    event.stopPropagation();
    this.contextMenuPosition.x = event.clientX + 'px';
    this.contextMenuPosition.y = event.clientY + 'px';
    this.selectedContextSection = section;
    this.selectedContextProject = section.projects[0] ?? null;
    this.selectedContextWell = null;
    this.sectionMenuTrigger.openMenu();
  }

  onWellContextMenu(event: MouseEvent, well: TreeWell): void {
    event.preventDefault();
    event.stopPropagation();
    this.contextMenuPosition.x = event.clientX + 'px';
    this.contextMenuPosition.y = event.clientY + 'px';
    this.selectedContextWell = well;
    this.wellMenuTrigger.openMenu();
  }

  createNewPartForProject(): void {
    if (!this.selectedContextProject) return;
    const dialogRef = this.dialog.open(CreateNewPartDialogComponent, {
      data: {
        projectId: this.selectedContextProject.id,
        projectName: this.selectedContextProject.label,
      },
      width: '720px',
      disableClose: true,
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result?.created) {
        this.reloadAndOpenNewProject(result);
      }
    });
  }

  copyProject(): void {
    if (!this.selectedContextProject) return;
    const dialogRef = this.dialog.open(CreateNewPartDialogComponent, {
      data: {
        projectId: this.selectedContextProject.id,
        projectName: this.selectedContextProject.label,
        isCopyingTheProject: true,
      },
      width: '720px',
      disableClose: true,
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result?.created) {
        this.reloadAndOpenNewProject(result);
      }
    });
  }

  private reloadAndOpenNewProject(result: any): void {
    this.isLoading = true;
    this.dashboardService.getHomeDashboard(this.selectedYear).subscribe({
      next: (response) => {
        const projects = response.projects;
        this.data = projects.filter(p => p.businessUnitCode !== 'TR');
        this.treeSections = this.buildTreeFromProjects(projects);
        this.isLoading = false;

        if (result.createdProjectId) {
          this.selectNewProjectInTree(result.createdProjectId);
          this.openProjectDetails(result.createdProjectId);
        }
      },
      error: (err) => {
        console.error('Failed to reload after create/copy', err);
        this.isLoading = false;
      },
    });
  }

  private selectNewProjectInTree(projectId: number): void {
    for (const section of this.treeSections) {
      section.expanded = true;
      for (const sbu of section.sbus) {
        for (const well of sbu.wells) {
          for (const project of well.projects) {
            if (project.id === projectId) {
              sbu.expanded = true;
              well.expanded = true;
              this.allExpanded = true;
              this.selectedNode = project.label;
              return;
            }
          }
        }
      }
      for (const project of section.projects) {
        if (project.id === projectId) {
          section.expanded = true;
          this.allExpanded = true;
          this.selectedNode = project.label;
          return;
        }
      }
    }
  }


  trashProject(): void {
    if (!this.selectedContextProject) return;
    const trashProjectRequest: TrashProjectRequest = {
      projectId: this.selectedContextProject.id,
      wellId: this.selectedContextProject.wellId,
    }
    this.isLoading = true;
    this.dashboardTreeService.trashProject(trashProjectRequest).subscribe({
      next: (response: any) => {
        this.isLoading = false;
        this.reloadAndOpenNewProject({
          created: true,
          createdProjectId: response?.projectId ?? undefined,
          wellId: response?.currentWellId ?? undefined,
        });
      },
      error: (err) => {
        this.isLoading = false;
        console.error('Failed to trash project', err);
      }
    });
  }

  viewWellAttachments(): void {
    if (!this.selectedContextWell) return;
    this.dialog.open(WellAttachmentsDialogComponent, {
      width: '800px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: {
        wellId: this.selectedContextWell.wellId,
        wellName: this.selectedContextWell.label,
      }
    });
  }

  generateWellRedesignReport(): void {
    if (!this.selectedContextWell) {
      return;
    }

    const reportUrl = this.router.serializeUrl(
      this.router.createUrlTree(['/well-redesign-report', this.selectedContextWell.wellId])
    );
    window.open(reportUrl, '_blank', 'noopener,noreferrer');
  }

  createNewProject(): void {
    const source = this.selectedContextProject ?? this.selectedContextWell?.projects?.[0];
    const wellId = source?.wellId ?? this.selectedContextWell?.wellId ?? 0;
    const wellName = source?.wellName ?? this.selectedContextWell?.label ?? '';
    const businessUnitCode = source?.businessUnitCode ?? '';
    const businessUnitName = source?.businessUnitName ?? '';

    this.createProjectWellId = wellId;
    this.createProjectWellName = wellName;
    this.createProjectBusinessUnitCode = businessUnitCode;
    this.createProjectBusinessUnitName = businessUnitName;
    this.selectedProjectId = null;
    this.isDetailsView = false;
    this.isCreateProjectView = true;
    this.router.navigate(['/projects-dashboard']);
  }

  createNewRnDProject(): void {
    const rdSection = this.treeSections.find(s => s.code === 'RD');
    const firstRdProject = rdSection?.projects?.[0];
    this.createProjectWellId = firstRdProject?.wellId ?? 0;
    this.createProjectWellName = 'R&D';
    this.createProjectBusinessUnitCode = 'RD';
    this.createProjectBusinessUnitName = 'R&D';
    this.selectedProjectId = null;
    this.isDetailsView = false;
    this.isCreateProjectView = true;
    this.router.navigate(['/projects-dashboard']);
  }

  openWellSearchDialog(): void {
    const businessUnits = this.treeSections
      .filter(s => s.code && s.code !== 'TR' && s.code !== 'RD')
      .flatMap(s => s.sbus.map(sbu => ({ code: sbu.code, name: sbu.label })));

    const uniqueUnits = Array.from(
      new Map(businessUnits.map(bu => [bu.code, bu])).values()
    );

    const dialogRef = this.dialog.open(WellSearchDialogComponent, {
      width: '60vw',
      disableClose: false,
      data: { businessUnits: uniqueUnits },
    });

    dialogRef.afterClosed().subscribe((result: WellSearchDialogResult | null) => {
      if (!result) return;
      this.createProjectWellId = result.wellId;
      this.createProjectWellName = result.wellName;
      this.createProjectBusinessUnitCode = result.businessUnitCode;
      this.createProjectBusinessUnitName = result.businessUnitName;
      this.selectedProjectId = null;
      this.isDetailsView = false;
      this.isCreateProjectView = true;
      this.router.navigate(['/projects-dashboard']);
    });
  }

  selectSection(section: TreeSection): void {
    this.ensureDashboardView();
    this.selectedNode = section.label;
    this.currentSelection = { type: 'section', section };
    if (section.code) {
      this.isLoading = true;
      if (this.currentView === 'projectSummary') {
        this.dashboardService.getProjectSummaryByUnit(this.selectedYear, section.code).subscribe({
          next: (projects) => { this.summaryData = projects; this.isLoading = false; },
          error: (err) => { console.error('Failed to load section summary data', err); this.isLoading = false; }
        });
      } else if (this.currentView === 'tests') {
        this.dashboardService.getTests(this.selectedYear, undefined, section.code).subscribe({
          next: (tests) => { this.testsData = tests; this.isLoading = false; },
          error: (err) => { console.error('Failed to load section tests data', err); this.isLoading = false; }
        });
      } else {
        this.dashboardService.getProjectsByUnit(this.selectedYear, section.code).subscribe({
          next: (projects) => { this.data = projects; this.isLoading = false; },
          error: (err) => { console.error('Failed to load section data', err); this.isLoading = false; }
        });
      }
    } else {
      // All SBUs → AllBusinessUnits API
      this.isLoading = true;
      if (this.currentView === 'projectSummary') {
        this.dashboardService.getProjectSummaryByYear(this.selectedYear).subscribe({
          next: (projects) => {
            this.summaryData = projects.filter(p => !p.rAndDProject && p.sbuCode !== 'RD');
            this.isLoading = false;
          },
          error: (err) => { console.error('Failed to load all SBU summary data', err); this.isLoading = false; }
        });
      } else if (this.currentView === 'tests') {
        this.dashboardService.getTests(this.selectedYear).subscribe({
          next: (tests) => { this.testsData = tests.filter(t => !t.rAndDTest); this.isLoading = false; },
          error: (err) => { console.error('Failed to load all SBUs tests data', err); this.isLoading = false; }
        });
      } else {
        this.dashboardService.getProjectsByYear(this.selectedYear).subscribe({
          next: (projects) => {
            this.data = projects.filter(p => !p.rAndDProject && p.businessUnitCode !== 'RD');
            this.treeSections = this.buildTreeFromProjects(projects);
            this.isLoading = false;
          },
          error: (err) => { console.error('Failed to load all SBU data', err); this.isLoading = false; }
        });
      }
    }
  }

  selectSbu(sbu: TreeSbu): void {
    this.ensureDashboardView();
    this.selectedNode = sbu.label;
    this.currentSelection = { type: 'sbu', sbu };
    this.isLoading = true;
    if (this.currentView === 'projectSummary') {
      this.dashboardService.getProjectSummaryByUnit(this.selectedYear, sbu.code).subscribe({
        next: (projects) => { this.summaryData = projects; this.isLoading = false; },
        error: (err) => { console.error('Failed to load unit summary data', err); this.isLoading = false; }
      });
    } else if (this.currentView === 'tests') {
      this.dashboardService.getTests(this.selectedYear, undefined, sbu.code).subscribe({
        next: (tests) => { this.testsData = tests; this.isLoading = false; },
        error: (err) => { console.error('Failed to load unit tests data', err); this.isLoading = false; }
      });
    } else {
      this.dashboardService.getProjectsByUnit(this.selectedYear, sbu.code).subscribe({
        next: (projects) => { this.data = projects; this.isLoading = false; },
        error: (err) => { console.error('Failed to load unit data', err); this.isLoading = false; }
      });
    }
  }

  selectWell(well: TreeWell): void {
    this.ensureDashboardView();
    this.selectedNode = well.label;
    this.currentSelection = { type: 'well', well };
    this.isLoading = true;
    if (this.currentView === 'projectSummary') {
      this.dashboardService.getProjectSummaryByWell(this.selectedYear, well.wellId).subscribe({
        next: (projects) => { this.summaryData = projects; this.isLoading = false; },
        error: (err) => { console.error('Failed to load well summary data', err); this.isLoading = false; }
      });
    } else if (this.currentView === 'tests') {
      this.dashboardService.getTests(this.selectedYear, well.wellId).subscribe({
        next: (tests) => { this.testsData = tests; this.isLoading = false; },
        error: (err) => { console.error('Failed to load well tests data', err); this.isLoading = false; }
      });
    } else {
      this.dashboardService.getProjectsByWell(this.selectedYear, well.wellId).subscribe({
        next: (projects) => { this.data = projects; this.isLoading = false; },
        error: (err) => { console.error('Failed to load well data', err); this.isLoading = false; }
      });
    }
  }


  get title(): string {
    switch (this.currentView) {
      case 'projects': return 'Projects Dashboard';
      case 'projectSummary': return 'Projects Summary Dashboard';
      case 'tests': return 'Tests Dashboard';
    }
  }

  get currentColumns(): GridColumn[] {
    switch (this.currentView) {
      case 'projects': return this.operationsColumns;
      case 'projectSummary': return this.projectSummaryColumns;
      case 'tests': return this.testsColumns;
    }
  }

  get currentData(): DashboardProject[] | ProjectSummary[] | DashboardTest[] {
    switch (this.currentView) {
      case 'projectSummary': return this.summaryData;
      case 'tests': return this.testsData;
      default: return this.data;
    }
  }

  switchView(view: DashboardView): void {
    this.currentView = view;
    this.reloadCurrentSelection();
  }

  openNotesDialog() {
    this.dialog.open(NotesDialogComponent, {
      width: '1000px',
      data: this.notesList
    });
  }

  onToolSelect(value: string): void {
    this.selectedTool = null;
    switch (value) {
      case 'addRig': this.openAddRigDialog(); break;
      case 'changeScalePort': this.openChangeScalePortDialog(); break;
      case 'manageMaterials': this.openManageMaterialsDialog(); break;
      case 'manageServiceCompanies': this.openManageServiceCompaniesDialog(); break;
      case 'notes': this.openNotesToolDialog(); break;
      case 'wellUpdate': this.openWellUpdateDialog(); break;
      case 'metrics': this.openMetricsDialog(); break;
      case 'buRedesignMetrics': this.openBuRedesignMetricsDialog(); break;
      case 'search': this.openSearchDialog(); break;
      case 'emailSubscriptions': this.openEmailSubscriptionsDialog(); break;
      case 'summaryReportByDate': this.openSummaryReportByDateDialog(); break;
      case 'materialsQAQC': this.openMaterialsQAQCDialog(); break;
      case 'cisProSearch': this.openCisProSearchDialog(); break;
    }
  }

  openAddRigDialog(): void {
    this.dialog.open(AddRigComponent, { width: '600px', disableClose: true });
  }

  openChangeScalePortDialog(): void {
    this.dialog.open(ChangeScalePortComponent, { width: '420px', disableClose: true });
  }

  openNotesToolDialog(): void {
    this.dialog.open(NotesNotificationComponent, { width: '750px', disableClose: true });
  }

  openWellUpdateDialog(): void {
    this.dialog.open(WellUpdateComponent, { width: '1000px', disableClose: true });
  }

  openMetricsDialog(): void {
    this.dialog.open(MetricsComponent, { width: '520px', disableClose: true });
  }

  openManageMaterialsDialog(): void {
    this.dialog.open(ManageMaterialsComponent, { width: '95vw', maxHeight: '90vh', disableClose: true });
  }

  openManageServiceCompaniesDialog(): void {
    this.dialog.open(ManageServiceCompaniesComponent, { width: '780px', maxHeight: '90vh', disableClose: true });
  }

  openBuRedesignMetricsDialog(): void {
    this.dialog.open(BuRedesignMetricsComponent, { width: '610px', disableClose: true });
  }

  openEmailSubscriptionsDialog(): void {
    this.dialog.open(EmailSubscriptionsComponent, { width: '800px', disableClose: true });
  }

  openSearchDialog(): void {
    this.dialog.open(SearchComponent, { width: '95vw', disableClose: true })
      .afterClosed().subscribe((result?: { projectId: number; receivedYear?: number }) => {
        if (!result?.projectId) return;
        const year = result.receivedYear;
        if (year && year !== this.selectedYear) {
          this.loadHomeData(year, () => {
            if (!this.years.includes(year)) {
              this.years = [...this.years, year].sort((a, b) => b - a);
            }
            this.selectedYear = year;
            this.openProjectDetails(result.projectId);
          });
        } else {
          this.openProjectDetails(result.projectId);
        }
      });
  }
  openMaterialsQAQCDialog() {
    this.dialog.open(MaterialsQAQC, { width: '90vw', maxWidth: '90vw', disableClose: true });
  }

  openSummaryReportByDateDialog(): void {
    this.dialog.open(SummaryReportByDateComponent, { width: '95vw', maxHeight: '90vh', disableClose: true });
  }

  openCisProSearchDialog(): void {
    const ref = this.dialog.open(CisProSearchComponent, { width: '95vw', maxHeight: '90vh', disableClose: true });
    ref.componentInstance.isOpen = true;
  }

  private reloadCurrentSelection(): void {
    const sel = this.currentSelection;
    if (this.currentView === 'projectSummary') {
      switch (sel.type) {
        case 'all':
          this.loadSummaryData(this.selectedYear);
          break;
        case 'section':
          if (sel.section.code) {
            this.isLoading = true;
            this.dashboardService.getProjectSummaryByUnit(this.selectedYear, sel.section.code).subscribe({
              next: (projects) => { this.summaryData = projects; this.isLoading = false; },
              error: (err) => { console.error('Failed to load section summary data', err); this.isLoading = false; }
            });
          } else {
            this.isLoading = true;
            this.dashboardService.getProjectSummaryByYear(this.selectedYear).subscribe({
              next: (projects) => {
                this.summaryData = projects.filter(p => !p.rAndDProject && p.sbuCode !== 'RD');
                this.isLoading = false;
              },
              error: (err) => { console.error('Failed to load all SBU summary data', err); this.isLoading = false; }
            });
          }
          break;
        case 'sbu':
          this.isLoading = true;
          this.dashboardService.getProjectSummaryByUnit(this.selectedYear, sel.sbu.code).subscribe({
            next: (projects) => { this.summaryData = projects; this.isLoading = false; },
            error: (err) => { console.error('Failed to load unit summary data', err); this.isLoading = false; }
          });
          break;
        case 'well':
          this.isLoading = true;
          this.dashboardService.getProjectSummaryByWell(this.selectedYear, sel.well.wellId).subscribe({
            next: (projects) => { this.summaryData = projects; this.isLoading = false; },
            error: (err) => { console.error('Failed to load well summary data', err); this.isLoading = false; }
          });
          break;
      }
    } else if (this.currentView === 'tests') {
      switch (sel.type) {
        case 'all':
          this.loadTestsData(this.selectedYear);
          break;
        case 'section':
          if (sel.section.code) {
            this.isLoading = true;
            this.dashboardService.getTests(this.selectedYear, undefined, sel.section.code).subscribe({
              next: (tests) => { this.testsData = tests; this.isLoading = false; },
              error: (err) => { console.error('Failed to load section tests data', err); this.isLoading = false; }
            });
          } else {
            this.loadTestsData(this.selectedYear);
          }
          break;
        case 'sbu':
          this.isLoading = true;
          this.dashboardService.getTests(this.selectedYear, undefined, sel.sbu.code).subscribe({
            next: (tests) => { this.testsData = tests; this.isLoading = false; },
            error: (err) => { console.error('Failed to load unit tests data', err); this.isLoading = false; }
          });
          break;
        case 'well':
          this.isLoading = true;
          this.dashboardService.getTests(this.selectedYear, sel.well.wellId).subscribe({
            next: (tests) => { this.testsData = tests; this.isLoading = false; },
            error: (err) => { console.error('Failed to load well tests data', err); this.isLoading = false; }
          });
          break;
      }
    } else {
      switch (sel.type) {
        case 'all':
          this.loadHomeData(this.selectedYear);
          break;
        case 'section':
          if (sel.section.code) {
            this.isLoading = true;
            this.dashboardService.getProjectsByUnit(this.selectedYear, sel.section.code).subscribe({
              next: (projects) => { this.data = projects; this.isLoading = false; },
              error: (err) => { console.error('Failed to load section data', err); this.isLoading = false; }
            });
          } else {
            this.isLoading = true;
            this.dashboardService.getProjectsByYear(this.selectedYear).subscribe({
              next: (projects) => {
                this.data = projects.filter(p => !p.rAndDProject && p.businessUnitCode !== 'RD');
                this.treeSections = this.buildTreeFromProjects(projects);
                this.isLoading = false;
              },
              error: (err) => { console.error('Failed to load all SBU data', err); this.isLoading = false; }
            });
          }
          break;
        case 'sbu':
          this.isLoading = true;
          this.dashboardService.getProjectsByUnit(this.selectedYear, sel.sbu.code).subscribe({
            next: (projects) => { this.data = projects; this.isLoading = false; },
            error: (err) => { console.error('Failed to load unit data', err); this.isLoading = false; }
          });
          break;
        case 'well':
          this.isLoading = true;
          this.dashboardService.getProjectsByWell(this.selectedYear, sel.well.wellId).subscribe({
            next: (projects) => { this.data = projects; this.isLoading = false; },
            error: (err) => { console.error('Failed to load well data', err); this.isLoading = false; }
          });
          break;
      }
    }
  }

  private loadTestsData(year: number): void {
    this.isLoading = true;
    this.dashboardService.getTests(year).subscribe({
      next: (tests) => {
        this.testsData = tests;
        this.isLoading = false;
      },
      error: (err) => { console.error('Failed to load tests data', err); this.isLoading = false; }
    });
  }

  private loadSummaryData(year: number): void {
    this.isLoading = true;
    this.dashboardService.getProjectSummaryByYear(year).subscribe({
      next: (projects) => {
        this.summaryData = projects;
        this.isLoading = false;
      },
      error: (err) => { console.error('Failed to load project summary data', err); this.isLoading = false; }
    });
  }

  ngAfterViewInit(): void {
    this.operationsColumns[0].customTemplate = this.statusCellTemplate;
    this.projectSummaryColumns[0].customTemplate = this.statusCellTemplate;
    this.testsColumns[0].customTemplate = this.testStatusCellTemplate;
    const trashColumn = this.operationsColumns.find(c => c.key === 'trashAction');
    if (trashColumn) {
      trashColumn.customTemplate = this.trashActionTemplate;
    }
  }

  getTestStatusColor(testStatus: number): string {
    switch (testStatus) {
      case 1: return 'gold';
      case 2: return 'green';
      case 3: return 'red';
      default: return 'black';
    }
  }

  getStatusColor(status: string): string {
    switch (status) {
      case 'Completed': return 'darkgray';
      case 'Draft':
      case 'Draft Again':
      case 'DraftAgain': return 'darkblue';
      case 'SenttoLab':
      case 'Sent to Lab': return 'green';
      case 'SentToLab Again': return 'pink';
      case 'Ongoing in Lab':
      case 'OngoingInLab': return 'gold';
      default: return 'black';
    }
  }

  openProjectDetails(projectId: number) {
    if (!this.isDashboardBaseRoute(this.router.url)) {
      void this.router.navigate(['/projects-dashboard'], {
        state: {
          openProjectId: projectId,
          projectInitialTab: 'project'
        }
      });
      return;
    }

    this.selectedProjectId = projectId;
    this.projectDetailsInitialTab = 'project';
    this.selectedCreateProjectId = null;
    this.isCreateProjectView = false;
    this.isSlurryDetailsView = false;
    this.isDetailsView = true;
    this.selectNewProjectInTree(projectId);
  }

  onTestViewRowClick(test: DashboardTest): void {
    if (test.isSlurry) {
      this.onOpenSlurry({
        slurryId: test.slurryId,
        projectId: test.projectId,
        projectType: 1,
        projectName: test.projectName,
        redesignComments: undefined,
      });
    } else {
      this.router.navigate(['/projects-dashboard/spacer', test.projectId, test.slurryId], {
        state: {
          projectId: test.projectId,
          projectName: test.projectName ?? '',
          slurry: test.isSlurry
        }
      });
    }
    this.selectNewProjectInTree(test.projectId);
  }

  /** Opens Spacer Details via child route, keeping the layout and highlighting the tree (mirrors onOpenSlurry). */
  onOpenSpacer(payload: {
    spacerId: number;
    projectId: number;
    projectName: string;
    blendTest: boolean;
    slurry: any;
    spacerQAQCTitles: any[];
  }): void {
    this.selectNewProjectInTree(payload.projectId);
    this.router.navigate(['/projects-dashboard/spacer', payload.projectId, payload.spacerId], {
      state: {
        projectId: payload.projectId,
        projectName: payload.projectName,
        blendTest: payload.blendTest,
        slurry: payload.slurry,
        spacerQAQCTitles: payload.spacerQAQCTitles ?? []
      }
    });
  }

  /** Opens Slurry Details inside the dashboard content area (keeps the layout). */
  onOpenSlurry(payload: {
    slurryId: number;
    slurryUfid?: string;
    projectId: number | null;
    projectType: number;
    projectName: string;
    redesignComments?: string;
  }): void {
    this.selectedSlurryId = payload.slurryId;
    this.selectedSlurryUfid = payload.slurryUfid ?? '';
    this.slurryProjectId = payload.projectId;
    this.slurryProjectName = payload.projectName ?? '';
    this.slurryProjectType = payload.projectType ?? 1;
    this.slurryRedesignComments = payload.redesignComments ?? '';
    this.isDetailsView = false;
    this.isCreateProjectView = false;
    this.isSlurryDetailsView = false;
    this.selectedProjectId = payload.projectId;

    void this.router.navigate(['/projects-dashboard', 'slurry-details', payload.slurryId], {
      state: {
        projectId: payload.projectId,
        slurryUfid: payload.slurryUfid ?? '',
        projectType: payload.projectType ?? 1,
        projectName: payload.projectName ?? '',
        redesign: payload.redesignComments != null
          ? { comments: payload.redesignComments }
          : undefined
      }
    });
  }

  /** Returns from Slurry Details back to Project Details (same project). */
  onSlurryBack(): void {
    this.isSlurryDetailsView = false;
    this.selectedSlurryId = null;
    this.projectDetailsInitialTab = 'project';
    if (this.selectedProjectId) {
      this.isDetailsView = true;
    }
  }

  onOpenProjectAttachmentsFromSlurry(): void {
    this.isSlurryDetailsView = false;
    this.selectedSlurryId = null;
    this.selectedProjectId = this.slurryProjectId;
    this.projectDetailsInitialTab = 'attachments';
    this.isDetailsView = true;
    if (this.slurryProjectId) {
      void this.router.navigate(['/projects-dashboard'], {
        state: {
          openProjectId: this.slurryProjectId,
          projectInitialTab: 'attachments'
        }
      });
      return;
    }

    this.router.navigate(['/projects-dashboard']);
  }

  closeCreateProjectView(createdProjectId?: number): void {
    this.isCreateProjectView = false;
    this.selectedCreateProjectId = null;
    if (createdProjectId) {
      this.reloadAndOpenNewProject({ createdProjectId });
    }
  }

  ensureDashboardView(): void {
    if (this.isDetailsView || this.isCreateProjectView || this.isSlurryDetailsView || !this.isDashboardBaseRoute(this.router.url)) {
      this.currentView = 'projects';
      this.isDetailsView = false;
      this.isCreateProjectView = false;
      this.isSlurryDetailsView = false;
      this.selectedProjectId = null;
      this.selectedCreateProjectId = null;
      this.selectedSlurryId = null;
      this.router.navigate(['/projects-dashboard']);
      this.reloadCurrentSelection();
    }
  }

  private syncEmbeddedViewsWithRoute(url: string): void {
    if (!this.isDashboardBaseRoute(url)) {
      this.isDetailsView = false;
      this.isCreateProjectView = false;
      this.isSlurryDetailsView = false;
    }
  }

  private isDashboardBaseRoute(url: string): boolean {
    const normalizedUrl = url.split('?')[0].split('#')[0];
    return normalizedUrl === '/projects-dashboard';
  }

  private applyDashboardRouteState(state: DashboardRouteState): void {
    if (!state?.openProjectId) {
      return;
    }

    this.selectedProjectId = state.openProjectId;
    this.projectDetailsInitialTab = state.projectInitialTab === 'attachments' ? 'attachments' : 'project';
    this.selectedCreateProjectId = null;
    this.isCreateProjectView = false;
    this.isSlurryDetailsView = false;
    this.isDetailsView = true;
    this.selectNewProjectInTree(state.openProjectId);
  }

  onProjectSaved(): void {
    this.reloadAndOpenNewProject({
      createdProjectId: this.selectedProjectId,
      wellId: this.selectedContextProject?.wellId,
    });
  }

  isTrashedProject(project: DashboardProject): boolean {
    return project.businessUnitCode === 'TR';
  }

  toggleTrashProject(project: DashboardProject, event: MouseEvent): void {
    event.stopPropagation();

    const isTrashed = this.isTrashedProject(project);
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '600px',
      data: {
        title: isTrashed ? 'Reverse Trash Project' : 'Trash Project',
        message: isTrashed
          ? `Are you sure you want to restore "${project.name}" from trash?`
          : `Are you sure you want to trash "${project.name}"?`,
        confirmLabel: isTrashed ? 'Restore' : 'Trash',
        cancelLabel: 'Cancel',
      },
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;
      this.selectedContextProject = {
        id: project.id,
        label: project.name,
        businessUnitCode: project.businessUnitCode,
        wellId: project.wellId,
        wellName: project.wellName,
        businessUnitName: project.businessUnitName,
      };
      this.trashProject();
    });
  }
}
